import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Customer } from '../../types/customer';
import { useAuth } from '../../contexts/AuthContext';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  BuildingOfficeIcon, 
  UserGroupIcon, 
  ArchiveBoxIcon 
} from '@heroicons/react/24/outline';

interface CustomerRelationshipLogFormProps {
  customer: Customer;
  onAddLog: (content: string, contactType: 'phone' | 'email' | 'distributor' | 'internal' | 'postal') => Promise<void>;
  onCancel: () => void;
  initialContent?: string;
  initialContactType?: 'phone' | 'email' | 'distributor' | 'internal' | 'postal';
  isEditing?: boolean;
}

const CustomerRelationshipLogForm: React.FC<CustomerRelationshipLogFormProps> = ({
  customer,
  onAddLog,
  onCancel,
  initialContent = '',
  initialContactType = 'phone',
  isEditing = false
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [content, setContent] = useState(initialContent);
  const [contactType, setContactType] = useState<'phone' | 'email' | 'distributor' | 'internal' | 'postal'>(initialContactType);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Update content and contactType if initial values change
  useEffect(() => {
    setContent(initialContent);
  }, [initialContent]);

  useEffect(() => {
    setContactType(initialContactType);
  }, [initialContactType]);

  const maxLength = 1000;

  // Kapcsolattípus opciók
  const contactTypeOptions = [
    {
      value: 'phone' as const,
      label: 'Telefon',
      icon: PhoneIcon,
      color: 'text-blue-600 bg-blue-50 border-blue-200'
    },
    {
      value: 'email' as const,
      label: 'Email',
      icon: EnvelopeIcon,
      color: 'text-green-600 bg-green-50 border-green-200'
    },
    {
      value: 'distributor' as const,
      label: 'Elosztói',
      icon: BuildingOfficeIcon,
      color: 'text-orange-600 bg-orange-50 border-orange-200'
    },
    {
      value: 'internal' as const,
      label: 'Belső',
      icon: UserGroupIcon,
      color: 'text-purple-600 bg-purple-50 border-purple-200'
    },
    {
      value: 'postal' as const,
      label: 'Postai',
      icon: ArchiveBoxIcon,
      color: 'text-gray-600 bg-gray-50 border-gray-200'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) {
      setError(t('customers.relationshipLog.contentRequired') || 'Content is required');
      return;
    }

    if (content.length > maxLength) {
      setError(t('customers.relationshipLog.maxLength') || `Maximum ${maxLength} characters`);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onAddLog(content, contactType);
      setContent('');
      setContactType('phone');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate example entry ID for display purposes
  const generateExampleEntryId = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;
    const seq = '0001';
    
    let prefix = '';
    if (customer.type === 'organization' && customer.taxNumber) {
      prefix = customer.taxNumber.substring(0, 8);
    } else {
      prefix = customer.id.substring(0, 8);
    }
    
    return `${prefix}_${dateStr}_${seq}`;
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <h3 className="text-lg font-medium text-gray-900 mb-3">
        {isEditing
          ? (t('customers.relationshipLog.edit') || 'Edit log entry')
          : (t('customers.relationshipLog.add') || 'Add new log entry')}
      </h3>
      
      {!isEditing && (
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <span className="text-sm font-medium text-gray-700 mr-2">
              {t('customers.relationshipLog.entryId') || 'Entry ID'}:
            </span>
            <span className="text-sm text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
              {generateExampleEntryId()}
            </span>
            <span className="text-xs text-gray-500 ml-2">(auto-generated)</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Kapcsolattípus választó */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('customers.relationshipLog.contactType.title') || 'Kapcsolattartás típusa'}
          </label>
          <div className="grid grid-cols-5 gap-2">
            {contactTypeOptions.map((option) => {
              const IconComponent = option.icon;
              const isSelected = contactType === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setContactType(option.value)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    isSelected 
                      ? option.color 
                      : 'border-gray-200 hover:border-gray-300 text-gray-600 bg-white'
                  }`}
                  disabled={isSubmitting}
                >
                  <IconComponent className="h-5 w-5 mx-auto mb-1" />
                  <div className="text-xs font-medium">
                    {option.label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-4">
          <label
            htmlFor="log-content"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {t('customers.relationshipLog.content') || 'Content'}
          </label>
          <textarea
            id="log-content"
            rows={4}
            className={`w-full rounded-md border ${
              error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            } shadow-sm p-2 text-sm`}
            placeholder={t('customers.relationshipLog.contentPlaceholder') || 'Enter log content...'}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={maxLength}
            disabled={isSubmitting}
          />
          <div className="flex justify-between mt-1">
            {error ? (
              <p className="text-sm text-red-600">{error}</p>
            ) : (
              <p className="text-xs text-gray-500">
                {t('customers.relationshipLog.maxLength') || `Maximum ${maxLength} characters`}
              </p>
            )}
            <p className="text-xs text-gray-500">
              {content.length}/{maxLength}
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('customers.relationshipLog.cancel') || 'Cancel'}
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t('common.submitting') || 'Saving...'
              : isEditing
                ? t('customers.relationshipLog.update') || 'Update entry'
                : t('customers.relationshipLog.save') || 'Save entry'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerRelationshipLogForm; 